import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import {
  getJogadores,
  getDadosJogo,
  clearAllData,
  clearJogoAtivo,
} from "../../../db/db";
import {
  incrementTurmaMemberStats,
  incrementUsuarioStats,
  updateTurmaJogoResumo,
} from "../../../service/jogoTurmaService";
import { getTurmaMembershipForUser } from "../../../service/turmaService";
import { ajustarOverallComDesempenho } from "../../../utils/performance";
import "./RelatorioFinal.css";

const BONUS_CONFIG = {
  MVP: 0.05,
  ARTILHEIRO: 0.04,
  ASSISTENTE: 0.03,
  SELECAO: 0.02,
  HAT_TRICK: 0.02,
  MAXIMO: 0.15,
};

const clampScore = (valor) => Math.min(Math.max(Number(valor) || 0, 0), 1);

const RelatorioFinal = () => {
  const navigate = useNavigate();
  const relatorioRef = useRef(null);
  const [jogadores, setJogadores] = useState([]);
  const [jogo, setJogo] = useState(null);
  const [artilheiro, setArtilheiro] = useState(null);
  const [liderAssistencias, setLiderAssistencias] = useState(null);
  const [selecaoDaRodada, setSelecaoDaRodada] = useState([]);
  const [sincronizando, setSincronizando] = useState(false);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const dadosJogadores = await getJogadores();
        const dadosJogo = await getDadosJogo();

        if (dadosJogo.length > 0) {
          setJogo(dadosJogo[0]);
        }

        if (dadosJogadores.length > 0) {
          const jogadoresPreparados = dadosJogadores.map(prepararJogador);
          // Ordena jogadores por pontos
          const jogadoresOrdenados = [...jogadoresPreparados].sort(
            (a, b) => calcularPontos(b) - calcularPontos(a)
          );
          setJogadores(jogadoresOrdenados);

          // Encontra artilheiro
          const artilheiroEncontrado = jogadoresPreparados.reduce(
            (prev, current) => (prev.gols > current.gols ? prev : current)
          );
          setArtilheiro(artilheiroEncontrado);

          // Encontra líder de assistências
          const liderAssist = jogadoresPreparados.reduce((prev, current) =>
            prev.assistencias > current.assistencias ? prev : current
          );
          setLiderAssistencias(liderAssist);

          // Monta seleção da rodada (top jogadores por pontuação)
          const tamanhoTime = dadosJogo[0]?.jogadoresPorTime || 5;
          const selecao = jogadoresOrdenados.slice(0, tamanhoTime);
          setSelecaoDaRodada(selecao);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };

    fetchDados();
  }, []);

  const calcularPontos = (jogador) =>
    jogador.gols * 2 +
    jogador.assistencias * 1 +
    jogador.vitorias * 3 +
    jogador.empates * 1;

  const formatarInfluencia = (valor) => Number(valor || 0).toFixed(2);
  const formatarPontuacao = (valor, casas = 3) =>
    Number(valor || 0).toFixed(casas);
  const formatarDeltaOverall = (valor) => {
    const numero = Number(valor || 0);
    if (!Number.isFinite(numero) || numero === 0) {
      return "0.00";
    }
    return `${numero > 0 ? "+" : ""}${numero.toFixed(2)}`;
  };

  const prepararJogador = (jogador) => {
    const partidas = jogador.qt_partidas || 0;
    const desempenhoTotal = jogador.desempenhoAcumulado || 0;
    const desempenhoMedio =
      partidas > 0 ? Number((desempenhoTotal / partidas).toFixed(3)) : 0;
    const influencia = Number(jogador.influenciaVitoria || 0);
    return {
      ...jogador,
      jogosSemTomarGol: jogador.jogosSemTomarGol || 0,
      influenciaVitoria: Number(influencia.toFixed(2)),
      desempenhoMedio,
    };
  };

  const construirJogadoresComMetricas = useCallback(() => {
    if (!Array.isArray(jogadores) || jogadores.length === 0) {
      return [];
    }
    const selecaoIds = new Set(selecaoDaRodada.map((jogador) => jogador.id));
    const campeaoId = jogadores[0]?.id || null;
    const artilheiroId = artilheiro?.id || null;
    const liderAssistId = liderAssistencias?.id || null;

    const calcularBonus = (jogadorAtual) => {
      if (!jogadorAtual?.id) {
        return 0;
      }
      let bonus = 0;
      if (campeaoId && jogadorAtual.id === campeaoId) {
        bonus += BONUS_CONFIG.MVP;
      }
      if (artilheiroId && jogadorAtual.id === artilheiroId) {
        bonus += BONUS_CONFIG.ARTILHEIRO;
      }
      if (liderAssistId && jogadorAtual.id === liderAssistId) {
        bonus += BONUS_CONFIG.ASSISTENTE;
      }
      if (selecaoIds.has(jogadorAtual.id)) {
        bonus += BONUS_CONFIG.SELECAO;
      }
      if ((jogadorAtual.gols || 0) >= 3) {
        bonus += BONUS_CONFIG.HAT_TRICK;
      }
      return clampScore(Math.min(bonus, BONUS_CONFIG.MAXIMO));
    };

    const manterOverall = (overallBase) => ({
      novoOverall: overallBase,
      delta: 0,
    });

    return jogadores.map((jogadorAtual) => {
      const partidas = jogadorAtual.qt_partidas || 0;
      const desempenhoMedio = jogadorAtual.desempenhoMedio || 0;
      const overallGlobalBase =
        typeof jogadorAtual.overallGlobal === "number"
          ? jogadorAtual.overallGlobal
          : 65;
      const overallTurmaBase =
        typeof jogadorAtual.overallTurma === "number"
          ? jogadorAtual.overallTurma
          : overallGlobalBase;
      const bonusDesempenho = partidas > 0 ? calcularBonus(jogadorAtual) : 0;
      const desempenhoComBonus =
        partidas > 0
          ? clampScore(desempenhoMedio + bonusDesempenho)
          : desempenhoMedio;

      const ajusteGlobal =
        partidas > 0
          ? ajustarOverallComDesempenho({
              overallAtual: overallGlobalBase,
              desempenhoReal: desempenhoComBonus,
            })
          : manterOverall(overallGlobalBase);
      const ajusteTurma =
        partidas > 0
          ? ajustarOverallComDesempenho({
              overallAtual: overallTurmaBase,
              desempenhoReal: desempenhoComBonus,
            })
          : manterOverall(overallTurmaBase);

      return {
        ...jogadorAtual,
        overallGlobalBase,
        overallTurmaBase,
        bonusDesempenho,
        desempenhoComBonus,
        overallGlobalAjustado: ajusteGlobal.novoOverall,
        overallTurmaAjustado: ajusteTurma.novoOverall,
        overallGlobalDelta: ajusteGlobal.delta || 0,
        overallTurmaDelta: ajusteTurma.delta || 0,
      };
    });
  }, [jogadores, selecaoDaRodada, artilheiro, liderAssistencias]);

  const jogadoresDetalhados = useMemo(
    () => construirJogadoresComMetricas(),
    [construirJogadoresComMetricas]
  );

  const sincronizarResultadosTurma = async () => {
    if (jogo?.contexto !== "turma" || !jogo?.jogoTurmaId) {
      return;
    }
    const jogadoresComMetricas = jogadoresDetalhados;
    if (jogadoresComMetricas.length === 0) {
      return;
    }

    const selecaoRodadaDetalhada = selecaoDaRodada.map((jogador) => {
      const referencia =
        jogadoresComMetricas.find((item) => item.id === jogador.id) || jogador;
      return {
        userId: referencia.id_usuario || null,
        nome: referencia.nome,
        gols: referencia.gols || 0,
        assistencias: referencia.assistencias || 0,
        vitorias: referencia.vitorias || 0,
        empates: referencia.empates || 0,
        derrotas: referencia.derrotas || 0,
        qt_partidas: referencia.qt_partidas || 0,
        jogosSemTomarGol: referencia.jogosSemTomarGol || 0,
        influenciaVitoria: Number(referencia.influenciaVitoria || 0),
        desempenhoMedio: referencia.desempenhoMedio || 0,
        pontos: calcularPontos(referencia),
      };
    });

    const snapshotJogadores = jogadoresComMetricas.map((jogador) => ({
      id_usuario: jogador.id_usuario || null,
      id_membro_turma: jogador.id_membro_turma || null,
      nome: jogador.nome,
      gols: jogador.gols || 0,
      assistencias: jogador.assistencias || 0,
      vitorias: jogador.vitorias || 0,
      empates: jogador.empates || 0,
      derrotas: jogador.derrotas || 0,
      qt_partidas: jogador.qt_partidas || 0,
      jogosSemTomarGol: jogador.jogosSemTomarGol || 0,
      influenciaVitoria: Number(jogador.influenciaVitoria || 0),
      desempenhoMedio: jogador.desempenhoMedio || 0,
      bonusDesempenho: jogador.bonusDesempenho || 0,
      desempenhoComBonus:
        jogador.desempenhoComBonus || jogador.desempenhoMedio || 0,
      overallTurmaBase: jogador.overallTurmaBase || null,
      overallTurmaFinal:
        jogador.overallTurmaAjustado ?? jogador.overallTurmaBase ?? null,
      overallTurmaDelta: jogador.overallTurmaDelta || 0,
      overallGlobalBase: jogador.overallGlobalBase || null,
      overallGlobalFinal:
        jogador.overallGlobalAjustado ?? jogador.overallGlobalBase ?? null,
      overallGlobalDelta: jogador.overallGlobalDelta || 0,
      pontos: calcularPontos(jogador),
    }));

    await updateTurmaJogoResumo({
      jogoId: jogo.jogoTurmaId,
      data: {
        status: 2,
        id_usuario_lider_assistencias: liderAssistencias?.id_usuario || null,
        id_usuario_mvp: jogadores[0]?.id_usuario || null,
        id_usuario_artilheiro: artilheiro?.id_usuario || null,
        selecao_rodada: selecaoRodadaDetalhada,
        relatorio_snapshot: {
          gerado_em: new Date().toISOString(),
          jogadores: snapshotJogadores,
        },
      },
    });

    const membershipCache = {};
    const membershipStats = {};
    const membershipOveralls = {};

    const getMembershipIdForPlayer = async (jogador) => {
      if (jogador.id_membro_turma) {
        return jogador.id_membro_turma;
      }
      if (!jogo?.turmaId || !jogador.id_usuario) {
        return null;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          membershipCache,
          jogador.id_usuario
        )
      ) {
        return membershipCache[jogador.id_usuario];
      }
      try {
        const membership = await getTurmaMembershipForUser({
          turmaId: jogo.turmaId,
          userId: jogador.id_usuario,
        });
        const membershipId = membership?.id || membership?.membershipId || null;
        membershipCache[jogador.id_usuario] = membershipId;
        return membershipId;
      } catch (error) {
        console.error(
          "Erro ao localizar participação do jogador na turma:",
          error
        );
        membershipCache[jogador.id_usuario] = null;
        return null;
      }
    };

    for (const jogador of jogadoresComMetricas) {
      const membershipId = await getMembershipIdForPlayer(jogador);
      if (!membershipId) continue;
      if (!membershipStats[membershipId]) {
        membershipStats[membershipId] = {
          gols: 0,
          assistencias: 0,
          vitorias: 0,
          empates: 0,
          derrotas: 0,
          qt_partidas: 0,
          jogosSemTomarGol: 0,
          influenciaVitoria: 0,
        };
      }
      membershipStats[membershipId].gols += jogador.gols || 0;
      membershipStats[membershipId].assistencias += jogador.assistencias || 0;
      membershipStats[membershipId].vitorias += jogador.vitorias || 0;
      membershipStats[membershipId].empates += jogador.empates || 0;
      membershipStats[membershipId].derrotas += jogador.derrotas || 0;
      membershipStats[membershipId].qt_partidas += jogador.qt_partidas || 0;
      membershipStats[membershipId].jogosSemTomarGol +=
        jogador.jogosSemTomarGol || 0;
      membershipStats[membershipId].influenciaVitoria += Number(
        jogador.influenciaVitoria || 0
      );
      membershipOveralls[membershipId] = jogador.overallTurmaAjustado;
    }

    for (const [membershipId, stats] of Object.entries(membershipStats)) {
      await incrementTurmaMemberStats({
        membershipId,
        stats,
        overall: membershipOveralls[membershipId],
      });
    }

    const usuariosAgregados = jogadoresComMetricas.reduce((acc, jogador) => {
      if (!jogador.id_usuario) {
        return acc;
      }
      if (!acc[jogador.id_usuario]) {
        acc[jogador.id_usuario] = {
          gols: 0,
          assistencias: 0,
          vitorias: 0,
          empates: 0,
          derrotas: 0,
          qt_partidas: 0,
          jogosSemTomarGol: 0,
          influenciaVitoria: 0,
        };
      }
      acc[jogador.id_usuario].gols += jogador.gols || 0;
      acc[jogador.id_usuario].assistencias += jogador.assistencias || 0;
      acc[jogador.id_usuario].vitorias += jogador.vitorias || 0;
      acc[jogador.id_usuario].empates += jogador.empates || 0;
      acc[jogador.id_usuario].derrotas += jogador.derrotas || 0;
      acc[jogador.id_usuario].qt_partidas += jogador.qt_partidas || 0;
      acc[jogador.id_usuario].jogosSemTomarGol += jogador.jogosSemTomarGol || 0;
      acc[jogador.id_usuario].influenciaVitoria += Number(
        jogador.influenciaVitoria || 0
      );
      return acc;
    }, {});
    const usuariosOveralls = {};
    jogadoresComMetricas.forEach((jogador) => {
      if (!jogador.id_usuario) return;
      usuariosOveralls[jogador.id_usuario] = jogador.overallGlobalAjustado;
    });

    for (const [userId, stats] of Object.entries(usuariosAgregados)) {
      await incrementUsuarioStats({
        userId,
        stats,
        overall: usuariosOveralls[userId],
      });
    }
  };

  const handleBaixarRelatorio = async () => {
    const elemento = relatorioRef.current;
    if (!elemento) return;

    try {
      const canvas = await html2canvas(elemento, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        useCORS: true,
      });

      const link = document.createElement("a");
      const dataAtual = new Date()
        .toLocaleDateString("pt-BR")
        .replace(/\//g, "-");
      link.download = `Relatorio_${jogo?.nome || "Amistoso"}_${dataAtual}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      alert("Erro ao gerar o relatório. Tente novamente.");
    }
  };

  const handleEncerrar = async () => {
    const confirmacao = window.confirm(
      "Tem certeza que deseja encerrar? Todos os dados serão apagados."
    );

    if (confirmacao) {
      try {
        setSincronizando(true);
        if (jogo?.contexto === "turma") {
          await sincronizarResultadosTurma();
        }
        await clearJogoAtivo();
        await clearAllData();
        localStorage.clear();
        alert("Sessão encerrada com sucesso!");
        navigate(jogo?.contexto === "turma" ? `/turmas/${jogo?.turmaId}` : "/");
      } catch (error) {
        console.error("Erro ao encerrar sessão:", error);
        alert("Ocorreu um erro ao encerrar a sessão.");
      } finally {
        setSincronizando(false);
      }
    }
  };

  return (
    <>
      <Header />

      <div className="body">
        <div className="center">
          <div className="body-content card">
            <div className="topo-page">
              <strong># Relatório Final - {jogo?.nome || "Amistoso"}</strong>
            </div>

            <div className="relatorio-content">
              {sincronizando && (
                <div className="relatorio-sync-overlay">
                  <div className="relatorio-sync-card">
                    <p>Sincronizando resultados no servidor...</p>
                    <small>
                      Por favor, não feche o aplicativo até finalizar.
                    </small>
                  </div>
                </div>
              )}
              {/* Template para Download (oculto) */}
              <div ref={relatorioRef} className="relatorio-download">
                <div className="header-download">
                  <h1>📊 Relatório Final</h1>
                  <h2>{jogo?.nome || "Amistoso"}</h2>
                  <p className="data-download">
                    {new Date().toLocaleDateString("pt-BR")}
                  </p>
                </div>

                {/* Destaques Compactos */}
                <div className="secao-download">
                  <h3>🏆 Destaques</h3>
                  <div className="destaques-download">
                    <div className="item-destaque">
                      <span className="label-destaque">⚽ Artilheiro:</span>
                      <span className="valor-destaque">
                        {artilheiro?.nome} ({artilheiro?.gols || 0} gols)
                      </span>
                    </div>
                    <div className="item-destaque">
                      <span className="label-destaque">🎯 Assistências:</span>
                      <span className="valor-destaque">
                        {liderAssistencias?.nome} (
                        {liderAssistencias?.assistencias || 0})
                      </span>
                    </div>
                    <div className="item-destaque">
                      <span className="label-destaque">👑 Campeão:</span>
                      <span className="valor-destaque">
                        {jogadores[0]?.nome} (
                        {calcularPontos(jogadores[0] || {})} pts)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Seleção da Rodada Compacta */}
                <div className="secao-download">
                  <h3>⭐ Seleção da Rodada (Top {selecaoDaRodada.length})</h3>
                  <div className="lista-download">
                    {selecaoDaRodada.map((jogador, index) => (
                      <div key={jogador.id} className="jogador-download">
                        <span className="pos-download">
                          {index === 0 && "🥇"}
                          {index === 1 && "🥈"}
                          {index === 2 && "🥉"}
                          {index > 2 && `${index + 1}º`}
                        </span>
                        <span className="nome-download">{jogador.nome}</span>
                        <span className="stats-download">
                          {jogador.gols}G | {jogador.assistencias}A |{" "}
                          {jogador.vitorias}V | {jogador.jogosSemTomarGol || 0}
                          CS | {formatarInfluencia(jogador.influenciaVitoria)}IV
                          | {calcularPontos(jogador)}pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ranking Compacto */}
                <div className="secao-download">
                  <h3>📋 Ranking Completo</h3>
                  <table className="tabela-download">
                    <thead>
                      <tr>
                        <th>Pos</th>
                        <th>Jogador</th>
                        <th>G</th>
                        <th>A</th>
                        <th>V</th>
                        <th>E</th>
                        <th>CS</th>
                        <th>IV</th>
                        <th>Pts</th>
                        <th>Des.</th>
                        <th>Bônus</th>
                        <th>OVR Base</th>
                        <th>OVR Pós</th>
                        <th>Δ OVR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jogadoresDetalhados.map((jogador, index) => (
                        <tr key={jogador.id}>
                          <td>
                            {index === 0 && "🥇"}
                            {index === 1 && "🥈"}
                            {index === 2 && "🥉"}
                            {index > 2 && index + 1}
                          </td>
                          <td>{jogador.nome}</td>
                          <td>{jogador.gols}</td>
                          <td>{jogador.assistencias}</td>
                          <td>{jogador.vitorias}</td>
                          <td>{jogador.empates}</td>
                          <td>{jogador.jogosSemTomarGol || 0}</td>
                          <td>
                            {formatarInfluencia(jogador.influenciaVitoria)}
                          </td>
                          <td>
                            <strong>{calcularPontos(jogador)}</strong>
                          </td>
                          <td>
                            {formatarPontuacao(jogador.desempenhoMedio, 2)}
                          </td>
                          <td>
                            {formatarPontuacao(jogador.bonusDesempenho, 3)}
                          </td>
                          <td>
                            {Number(jogador.overallTurmaBase || 65).toFixed(1)}
                          </td>
                          <td>
                            {Number(
                              jogador.overallTurmaAjustado ??
                                jogador.overallTurmaBase ??
                                65
                            ).toFixed(1)}
                          </td>
                          <td>
                            {formatarDeltaOverall(jogador.overallTurmaDelta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rodape-download">
                  <p>
                    Sistema de Pontuação: Gol = 2pts | Assistência = 1pt |
                    Vitória = 3pts | Empate = 1pt
                  </p>
                  <p className="app-nome">PALM3 - Gerenciador de Amistosos</p>
                </div>
              </div>

              {/* Destaques */}
              <div className="destaques">
                <h2>🏆 Destaques</h2>
                <div className="destaque-grid">
                  <div className="destaque-card artilheiro">
                    <h3>⚽ Artilheiro</h3>
                    <p className="nome">{artilheiro?.nome || "-"}</p>
                    <p className="stats">{artilheiro?.gols || 0} gols</p>
                  </div>
                  <div className="destaque-card assistente">
                    <h3>🎯 Assistências</h3>
                    <p className="nome">{liderAssistencias?.nome || "-"}</p>
                    <p className="stats">
                      {liderAssistencias?.assistencias || 0} assistências
                    </p>
                  </div>
                  <div className="destaque-card campeao">
                    <h3>👑 Campeão</h3>
                    <p className="nome">{jogadores[0]?.nome || "-"}</p>
                    <p className="stats">
                      {calcularPontos(jogadores[0] || {})} pontos
                    </p>
                  </div>
                </div>
              </div>

              {/* Seleção da Rodada */}
              <div className="selecao-rodada">
                <h2>⭐ Seleção da Rodada</h2>
                <p className="subtitulo-selecao">
                  Os {selecaoDaRodada.length} jogadores com melhor pontuação
                </p>
                <div className="lista-selecao">
                  {selecaoDaRodada.map((jogador, index) => (
                    <div key={jogador.id} className="jogador-selecao">
                      <div className="posicao-selecao">
                        {index === 0 && "🥇"}
                        {index === 1 && "🥈"}
                        {index === 2 && "🥉"}
                        {index > 2 && `${index + 1}º`}
                      </div>
                      <div className="info-jogador-selecao">
                        <span className="nome-selecao">{jogador.nome}</span>
                        <div className="stats-selecao">
                          <span>{jogador.gols}G</span>
                          <span>{jogador.assistencias}A</span>
                          <span>{jogador.vitorias}V</span>
                          <span>{jogador.jogosSemTomarGol || 0}CS</span>
                          <span>
                            {formatarInfluencia(jogador.influenciaVitoria)}IV
                          </span>
                          <span className="pontos-selecao">
                            {calcularPontos(jogador)} pts
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ranking Completo */}
              <div className="ranking-completo">
                <h2>📋 Ranking Completo</h2>
                <table className="tabela-ranking">
                  <thead>
                    <tr>
                      <th>POS</th>
                      <th>NOME</th>
                      <th>GOLS</th>
                      <th>ASS</th>
                      <th>VIT</th>
                      <th>EMP</th>
                      <th>CS</th>
                      <th>IV</th>
                      <th>PTS</th>
                      <th>DES.</th>
                      <th>BÔNUS</th>
                      <th>OVR BASE</th>
                      <th>OVR PÓS</th>
                      <th>Δ OVR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jogadoresDetalhados.map((jogador, index) => (
                      <tr
                        key={jogador.id}
                        className={index < 3 ? `top-${index + 1}` : ""}
                      >
                        <td className="posicao">
                          {index === 0 && "🥇"}
                          {index === 1 && "🥈"}
                          {index === 2 && "🥉"}
                          {index > 2 && index + 1}
                        </td>
                        <td className="nome-jogador">{jogador.nome}</td>
                        <td>{jogador.gols}</td>
                        <td>{jogador.assistencias}</td>
                        <td>{jogador.vitorias}</td>
                        <td>{jogador.empates}</td>
                        <td>{jogador.jogosSemTomarGol || 0}</td>
                        <td>{formatarInfluencia(jogador.influenciaVitoria)}</td>
                        <td className="pontos">
                          <strong>{calcularPontos(jogador)}</strong>
                        </td>
                        <td>{formatarPontuacao(jogador.desempenhoMedio, 2)}</td>
                        <td>{formatarPontuacao(jogador.bonusDesempenho, 3)}</td>
                        <td>
                          {Number(jogador.overallTurmaBase || 65).toFixed(1)}
                        </td>
                        <td>
                          {Number(
                            jogador.overallTurmaAjustado ??
                              jogador.overallTurmaBase ??
                              65
                          ).toFixed(1)}
                        </td>
                        <td>
                          {formatarDeltaOverall(jogador.overallTurmaDelta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Legenda de Pontuação */}
              <div className="legenda-pontuacao">
                <h3>Sistema de Pontuação:</h3>
                <p>
                  Gol = 2 pts | Assistência = 1 pt | Vitória = 3 pts | Empate =
                  1 pt
                </p>
              </div>

              {/* Botão Encerrar */}
              <div className="botoes-finais">
                <button
                  className="btn-voltar-jogo"
                  onClick={() => navigate("/novo-amistoso/jogo-em-andamento")}
                >
                  🔄 Voltar ao Jogo
                </button>
                <button
                  className="btn-download"
                  onClick={handleBaixarRelatorio}
                >
                  📥 Baixar Relatório
                </button>
                <button className="btn-encerrar" onClick={handleEncerrar}>
                  Encerrar e Voltar ao Início
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default RelatorioFinal;
